import {
  CHARGEBEE_APPLE_PAY_BUTTON_OPTIONS,
  CHARGEBEE_APPLE_PAY_PAYMENT_METHOD_TYPE,
  CHARGEBEE_PAYMENT_COMPONENT_SAVE_OPTIONS,
  CHARGEBEE_PAYMENT_COMPONENT_STYLE,
  CHARGEBEE_SCRIPT_ID,
  CHARGEBEE_SDK_URL,
} from '@/constants/chargebee';
import type {
  Chargebee3DSHandler,
  ChargebeeApplePayButtonOptions,
  ChargebeeApplePayRecurringPaymentRequest,
  ChargebeeAuthorizeAdditionalData,
  ChargebeeCardComponent,
  ChargebeeCardComponentOptions,
  ChargebeeInstance,
  ChargebeeComponents,
  ChargebeePaymentButtonComponent,
  ChargebeePaymentComponentContext,
  ChargebeePaymentComponentError,
  ChargebeePaymentIntent,
  ChargebeePaymentIntentEnvelope,
  ChargebeeWalletPaymentResult,
} from '@/types/chargebee';
import type { ChargebeePK } from '@/types/subscription';
import { loadScriptOnce } from '@/utils/nuvei';
import { logError } from '@/utils/logger';
import { isChargebeePaymentIntentRejection, withChargebeePaymentMethodType } from '@/utils/chargebeePaymentIntent';

interface MountWalletButtonParams {
  selector: string;
  paymentIntent: ChargebeePaymentIntent;
}

interface MountApplePayButtonParams extends MountWalletButtonParams {
  /** Absent for a one-off charge; every subscription should pass one. */
  recurringPaymentRequest?: ChargebeeApplePayRecurringPaymentRequest;
}

/**
 * What a mounted wallet button exposes to its React form. Deliberately narrow:
 * the forms own state and re-arming, the service owns every SDK call.
 */
export interface ChargebeeWalletButton {
  /** Resolves once the shopper authorizes; rejects on dismissal or failure. */
  awaitPayment: () => Promise<ChargebeeWalletPaymentResult>;
  /** Re-syncs Chargebee's copy of the intent after a server-side amount change. */
  updatePaymentIntent: (paymentIntent: ChargebeePaymentIntent) => void;
}

interface MountPaymentComponentParams {
  selector: string;
  paymentIntent: ChargebeePaymentIntent;
  /** Chargebee `payment_method_type` the component is restricted to (e.g. `kakao_pay`). */
  paymentMethodType: string;
  locale?: string;
  /** Shopper email / billing country; some methods (Kakao Pay) won't authorize without the email. */
  context?: ChargebeePaymentComponentContext;
  /**
   * Host for Chargebee's own Pay button ({@link ChargebeePaymentButtonComponent}).
   * Redirect methods NEED it: only a click inside a `js.chargebee.com` frame
   * gives the component frame the user activation `window.open` requires.
   */
  button?: { selector: string; text: string };
  /**
   * Runs before Chargebee's button confirms. Reject to stop the submit — the
   * form's own fields (email, ZIP) are validated here.
   */
  onButtonClick?: () => Promise<void>;
  /**
   * Fires for an authorization the component ran on its OWN button (wallet-
   * style methods render one) — i.e. with no `authorize()` in flight.
   */
  onAuthorized?: (paymentIntent: ChargebeePaymentIntent) => void;
  /** Same, for a failure with no `authorize()` in flight. */
  onFailed?: (error: ChargebeePaymentComponentError) => void;
  /**
   * Fires each time the drop-in reports its fields drawn (`onPaymentMethodChange`).
   * Never fires for a redirect method with no fields (Kakao Pay, Naver Pay), so
   * the caller can collapse an empty drop-in.
   */
  onFieldsRendered?: () => void;
  /**
   * Abort when the caller unmounts mid-mount: the readiness wait can run for
   * seconds, and a stale mount that outlives its form would otherwise clear
   * the button host a NEWER form (another APM) has since claimed.
   */
  signal?: AbortSignal;
}

/**
 * What a mounted Payment Component exposes to its React form — the same narrow
 * contract as {@link ChargebeeWalletButton}, plus the custom-button path.
 */
export interface ChargebeeMountedPaymentComponent {
  /** Surfaces Chargebee's inline field errors; `false` means don't submit yet. */
  validate: () => Promise<boolean>;
  /**
   * Custom Pay button path: `validate()` → `confirm()` → the intent
   * `onSuccess` delivers. Rejects with {@link ChargebeePaymentComponentInvalidError}
   * when validation fails (Chargebee has already drawn the inline errors), or
   * with the `onError` payload / thrown error otherwise.
   */
  authorize: () => Promise<ChargebeePaymentIntent>;
  updatePaymentIntent: (paymentIntent: ChargebeePaymentIntent) => void;
  /**
   * Re-syncs shopper data (email typed after mount, ZIP change); remounts the
   * drop-in. Resolves once the remounted fields are drawn again (or after the
   * readiness timeout), so a caller can flush before `confirm()`.
   */
  updateContext: (context: ChargebeePaymentComponentContext) => Promise<void>;
  /** Relabels Chargebee's button (total changed after a tax recalc); no-op without one. */
  updateButtonText: (text: string) => void;
  close: () => void;
}

/** `authorize()` rejection when the form has invalid fields — not a decline. */
export class ChargebeePaymentComponentInvalidError extends Error {
  constructor() {
    super('Chargebee payment component has invalid fields');
    this.name = 'ChargebeePaymentComponentInvalid';
  }
}

/**
 * Single owner of the Chargebee.js surface — script load, init, module loads,
 * card component, payment intents and both wallet buttons. Mirrors the Adyen
 * services (`adyenService`, `adyenApplePayService`): components and hooks stay
 * React-only and never touch `window.Chargebee` or a raw SDK handler.
 */
class ChargebeeService {
  private instance: ChargebeeInstance | null = null;
  // Chargebee.init() sets a page-global singleton; re-initializing on remount
  // logs warnings and can orphan mounted iframes. Caching the whole readiness
  // chain (script → init → components module) keeps remounts on one instance.
  private readyInstance: Promise<ChargebeeInstance> | null = null;
  // Chargebee allows ONE `payment` component per page ("we don't support
  // multiple components of same type, yet"): a mount that starts while the
  // previous one is still coming up — or tearing down after an abort — throws.
  // Settles when that previous mount does (either way); a MOUNTED component is
  // released by its form's `close()`, which runs before the next form mounts.
  private lastPaymentComponentMount: Promise<unknown> = Promise.resolve();

  initialize({ siteId, publicKey }: ChargebeePK): Promise<ChargebeeInstance> {
    if (!this.readyInstance) {
      this.readyInstance = this.createInstance(siteId, publicKey).catch((error: unknown) => {
        // Don't cache the failure — a remount (or a retry) gets a clean attempt.
        this.readyInstance = null;
        throw error;
      });
    }
    return this.readyInstance;
  }

  private async createInstance(siteId: string, publicKey: string): Promise<ChargebeeInstance> {
    // Already on the page (preloaded, or a second route mount) — skip the fetch.
    if (!window.Chargebee) {
      await loadScriptOnce(CHARGEBEE_SDK_URL, CHARGEBEE_SCRIPT_ID);
    }
    if (!window.Chargebee) {
      throw new Error('Chargebee SDK unavailable after script load');
    }
    const instance = window.Chargebee.init({ site: siteId, publishableKey: publicKey });
    // Chargebee asserts the `components` module before any card-component call;
    // without this `createComponent('card')` throws a bare `modules not loaded`
    // that takes the whole route to the error boundary.
    await instance.load('components');
    this.instance = instance;
    return instance;
  }

  getInstance(): ChargebeeInstance | null {
    return this.instance;
  }

  createCardComponent(options: ChargebeeCardComponentOptions): ChargebeeCardComponent {
    return this.requireInstance().createComponent('card', options);
  }

  /**
   * Chargebee owns the ENTIRE 3DS flow here — challenge included — and resolves
   * the intent in `authorized` state.
   */
  authorizeCardWith3ds(
    cardComponent: ChargebeeCardComponent,
    paymentIntent: ChargebeePaymentIntent,
    additionalData: ChargebeeAuthorizeAdditionalData
  ): Promise<ChargebeePaymentIntent> {
    return cardComponent.authorizeWith3ds(paymentIntent, additionalData);
  }

  /**
   * Expands a payment-intent id into the full Chargebee object. Chargebee only
   * exposes this through the 3DS handler, which needs its module loaded first.
   */
  async retrievePaymentIntent(paymentIntentId: string): Promise<ChargebeePaymentIntent> {
    const instance = this.requireInstance();
    await instance.load('3ds-handler');
    const handler: Chargebee3DSHandler = instance.create3DSHandler();
    const response = await handler.retrievePaymentIntent(paymentIntentId);
    const envelope = response as ChargebeePaymentIntentEnvelope;
    return envelope?.payment_intent ?? (response as ChargebeePaymentIntent);
  }

  async mountGooglePayButton({ selector, paymentIntent }: MountWalletButtonParams): Promise<ChargebeeWalletButton> {
    const googlePay = await this.requireInstance().load('google-pay');
    googlePay.setPaymentIntent(paymentIntent);
    clearWalletContainer(selector);
    await googlePay.mountPaymentButton(selector, {
      buttonColor: 'black',
      buttonType: 'plain',
      buttonSizeMode: 'fill',
    });
    return {
      awaitPayment: () => googlePay.handlePayment(),
      updatePaymentIntent: (paymentIntent) => googlePay.updatePaymentIntent(paymentIntent),
    };
  }

  /**
   * Apple Pay through Chargebee's standalone handler — the exact sibling of
   * {@link mountGooglePayButton}, so the button is ours to wrap and Apple's
   * sheet is opened by our own origin.
   *
   * Support is checked BEFORE mounting because the alternative (v2's drop-in)
   * answers "this device can't do Apple Pay" by rendering the site's remaining
   * payment methods instead — a second checkout inside our Apple Pay box.
   * Rejecting here is what collapses the box, same as every other wallet.
   */
  async mountApplePayButton({
    selector,
    paymentIntent,
    recurringPaymentRequest,
  }: MountApplePayButtonParams): Promise<ChargebeeWalletButton> {
    const applePay = await this.requireInstance().load('apple-pay');
    if (applePay.supportsApplePay?.() === false) {
      throw new Error('Apple Pay is not supported in this browser');
    }
    // The schedule has to be in place before the sheet opens: Apple decides
    // MPAN vs DPAN at authorization and nothing can revise it afterwards.
    const options: ChargebeeApplePayButtonOptions = {
      ...CHARGEBEE_APPLE_PAY_BUTTON_OPTIONS,
      ...(recurringPaymentRequest ? { recurringPaymentRequest } : {}),
    };

    const walletIntent = withChargebeePaymentMethodType(paymentIntent, CHARGEBEE_APPLE_PAY_PAYMENT_METHOD_TYPE);
    const mountWith = async (intent: ChargebeePaymentIntent): Promise<void> => {
      applePay.setPaymentIntent(intent);
      clearWalletContainer(selector);
      await applePay.mountPaymentButton(selector, options);
    };

    let declaresWalletType = true;
    try {
      await mountWith(walletIntent);
    } catch (error) {
      // A site that won't take the declared type must still get its button — but
      // ONLY that failure is retried with the intent verbatim. Anything else
      // (no container, bad schedule, no Apple Pay on the device) would otherwise
      // mount a wallet whose authorization is recorded as `card`.
      if (walletIntent === paymentIntent || !isChargebeePaymentIntentRejection(error)) throw error;
      logError('Chargebee rejected the apple_pay payment intent type, mounting with the session intent', error);
      declaresWalletType = false;
      await mountWith(paymentIntent);
    }

    return {
      awaitPayment: () => applePay.handlePayment(),
      updatePaymentIntent: (paymentIntent) =>
        applePay.updatePaymentIntent(
          declaresWalletType
            ? withChargebeePaymentMethodType(paymentIntent, CHARGEBEE_APPLE_PAY_PAYMENT_METHOD_TYPE)
            : paymentIntent
        ),
    };
  }

  /**
   * Payment Component for the long tail of APMs — ONE method per mount
   * (`allowed: [type]`), so the drop-in can never fall back to the site's other
   * methods inside our box. `confirm()` resolves with nothing and the result
   * arrives through the `onSuccess` / `onError` callbacks, so the mounted handle
   * turns them back into a promise for the custom Pay button, and routes any
   * callback that fires with no `authorize()` pending (Chargebee-rendered
   * wallet buttons) to the caller's handlers.
   */
  async mountPaymentComponent(params: MountPaymentComponentParams): Promise<ChargebeeMountedPaymentComponent> {
    const instance = this.requireInstance();
    const mount = awaitPreviousPaymentComponentMount(this.lastPaymentComponentMount, params.paymentMethodType).then(
      () => this.doMountPaymentComponent(instance, params)
    );
    this.lastPaymentComponentMount = mount.catch(() => undefined);
    return mount;
  }

  private async doMountPaymentComponent(
    instance: ChargebeeInstance,
    {
      selector,
      paymentIntent,
      paymentMethodType,
      locale,
      context,
      button,
      onButtonClick,
      onAuthorized,
      onFailed,
      onFieldsRendered,
      signal,
    }: MountPaymentComponentParams
  ): Promise<ChargebeeMountedPaymentComponent> {
    throwIfAborted(signal);
    let pending: {
      resolve: (intent: ChargebeePaymentIntent) => void;
      reject: (error: unknown) => void;
    } | null = null;

    // The button must come from the SAME `components` object to drive this component.
    const components = instance.components({ style: CHARGEBEE_PAYMENT_COMPONENT_STYLE, locale });
    // `mount()` resolves as soon as the iframe can talk SDK; Chargebee's docs
    // send you to `onAppReady` (method list + dictionary loaded) and
    // `onPaymentMethodChange` (the default method is selected and its fields
    // are rendered) for "actually on screen". Both are awaited below so the
    // caller gets ONE ready signal for a fully drawn drop-in. `fieldsRendered`
    // is re-armed by `updateContext`, whose remount draws the fields again.
    // A redirect method with no fields (Kakao Pay, Naver Pay) NEVER reports
    // `onPaymentMethodChange`, so it is only ever given a short grace.
    const appReady = deferred();
    let fieldsRendered = deferred();
    let hasRenderedFields = false;
    const component = components.create(
      'payment',
      {
        paymentIntent: { id: paymentIntent.id },
        layout: { type: 'tab', showRadioButtons: false },
        paymentMethods: {
          allowed: [paymentMethodType],
          sortOrder: [paymentMethodType],
          selectDefaultPaymentMethod: true,
          savePaymentMethod: CHARGEBEE_PAYMENT_COMPONENT_SAVE_OPTIONS,
        },
        ...(context ? { context } : {}),
      },
      {
        onSuccess: (intent) => {
          if (pending) {
            pending.resolve(intent);
            pending = null;
            return;
          }
          onAuthorized?.(intent);
        },
        onError: (error) => {
          if (pending) {
            pending.reject(error);
            pending = null;
            return;
          }
          onFailed?.(error);
        },
        onAppReady: async () => {
          appReady.resolve();
          return true;
        },
        onPaymentMethodChange: () => {
          hasRenderedFields = true;
          fieldsRendered.resolve();
          onFieldsRendered?.();
        },
        ...(onButtonClick ? { onButtonClick } : {}),
      }
    );

    clearWalletContainer(selector);
    if (!(await component.mount(selector))) {
      throw new Error('Chargebee payment component failed to mount');
    }
    throwIfAborted(signal, component);

    // Chargebee's button learns that the component is ready only through the
    // handshake it runs when IT mounts, and the component answers "not yet"
    // until `onAppReady` — with `selectDefaultPaymentMethod` it never re-sends,
    // so a button mounted earlier stays a disabled spinner for good.
    await awaitPaymentComponentSignal(appReady.promise, {
      signal,
      timeoutMs: CHARGEBEE_APP_READY_TIMEOUT_MS,
      onTimeout: () => warnNeverReported('onAppReady', paymentMethodType, CHARGEBEE_APP_READY_TIMEOUT_MS),
    });
    throwIfAborted(signal, component);
    // Fields, when the method has any, are drawn right behind `onAppReady`;
    // holding for them lets the form reveal drop-in and button together instead
    // of one after the other. A field-less method never reports them, hence a
    // grace rather than a wait, and no warning.
    await awaitPaymentComponentSignal(fieldsRendered.promise, { signal, timeoutMs: CHARGEBEE_FIELDS_RENDER_GRACE_MS });
    throwIfAborted(signal, component);
    const buttonComponent = button ? await mountPaymentButton(components, button, paymentMethodType, onFailed) : null;
    if (button && !buttonComponent) {
      component.close();
      throw new Error('Chargebee payment button failed to mount');
    }

    return {
      validate: () => component.validate(),
      authorize: async () => {
        // One resolver slot: a second confirm on the same single-use intent
        // would orphan the first caller's promise.
        if (pending) {
          throw new Error('Chargebee payment component authorization already in flight');
        }
        // Claimed BEFORE the first await so a concurrent call sees it.
        const result = new Promise<ChargebeePaymentIntent>((resolve, reject) => {
          pending = { resolve, reject };
        });
        try {
          if (!(await component.validate())) {
            throw new ChargebeePaymentComponentInvalidError();
          }
          await component.confirm();
        } catch (error) {
          pending = null;
          throw error;
        }
        return result;
      },
      updatePaymentIntent: (intent) => component.update({ paymentIntent: { id: intent.id } }),
      updateContext: (nextContext) => {
        fieldsRendered = deferred();
        component.update({ context: nextContext });
        // Nothing to redraw for a field-less method — it would never report back.
        if (!hasRenderedFields) return Promise.resolve();
        return awaitPaymentComponentSignal(fieldsRendered.promise, {
          timeoutMs: CHARGEBEE_CONTEXT_SYNC_TIMEOUT_MS,
          onTimeout: () =>
            warnNeverReported('onPaymentMethodChange', paymentMethodType, CHARGEBEE_CONTEXT_SYNC_TIMEOUT_MS),
        });
      },
      updateButtonText: (text) =>
        buttonComponent?.update({ paymentMethods: { options: { [paymentMethodType]: { text } } } }),
      close: () => {
        buttonComponent?.close();
        component.close();
      },
    };
  }

  private requireInstance(): ChargebeeInstance {
    if (!this.instance) {
      throw new Error('Chargebee SDK is not initialized');
    }
    return this.instance;
  }

  /** Test seam / route teardown — the next `initialize` starts from scratch. */
  reset(): void {
    this.instance = null;
    this.readyInstance = null;
    this.lastPaymentComponentMount = Promise.resolve();
  }
}

export const chargebeeService = new ChargebeeService();

/**
 * Wallet handlers are module singletons with no unmount, and the wallets now sit
 * in selectable method boxes — switching away and back mounts a second button on
 * the same handler, into a fresh container. Chargebee's own drop-in empties the
 * container before every `mountPaymentButton` for exactly this reason; without
 * it a surviving iframe can leave two buttons, or one wired to a dead node.
 * The Payment Component mount shares the hygiene for the same reason.
 *
 * A missing container is left to Chargebee, whose mount rejects with
 * `applePayElementNotFound` — a clearer signal than anything we'd invent.
 */
const clearWalletContainer = (selector: string): void => {
  document.querySelector(selector)?.replaceChildren();
};

const deferred = (): { promise: Promise<void>; resolve: () => void } => {
  let resolve: () => void = () => undefined;
  const promise = new Promise<void>((res) => {
    resolve = res;
  });
  return { promise, resolve };
};

/** Upper bound on waiting for `onAppReady`; past it the mount continues anyway (today's behaviour). */
export const CHARGEBEE_APP_READY_TIMEOUT_MS = 15_000;
/** How long after `onAppReady` the fields get to report drawn before a field-less method is revealed as is. */
export const CHARGEBEE_FIELDS_RENDER_GRACE_MS = 1_000;
/** Same, for the redraw after `updateContext` — a Pay click is waiting on it, so it is kept short. */
export const CHARGEBEE_CONTEXT_SYNC_TIMEOUT_MS = 5_000;
/** Upper bound on waiting for the previous mount to settle before a new one is tried anyway. */
export const CHARGEBEE_MOUNT_HANDOVER_TIMEOUT_MS = 10_000;

/**
 * Resolves on the callback or after the timeout (running `onTimeout` first),
 * whichever comes first. An abort resolves at once (silently); the caller
 * decides what abort means.
 */
const awaitPaymentComponentSignal = (
  ready: Promise<void>,
  { signal, timeoutMs, onTimeout }: { signal?: AbortSignal; timeoutMs: number; onTimeout?: () => void }
): Promise<void> =>
  new Promise((resolve) => {
    const settle = () => {
      clearTimeout(timer);
      signal?.removeEventListener('abort', settle);
      resolve();
    };
    const timer = setTimeout(() => {
      onTimeout?.();
      settle();
    }, timeoutMs);
    if (signal?.aborted) return settle();
    signal?.addEventListener('abort', settle, { once: true });
    void ready.then(settle);
  });

/**
 * A component that never reports ready is broken either way, and revealing it
 * (and mounting its button) then is no worse than never doing so.
 */
const warnNeverReported = (
  callback: 'onAppReady' | 'onPaymentMethodChange',
  paymentMethodType: string,
  timeoutMs: number
): void =>
  logError(`Chargebee payment component never reported ${callback}, continuing anyway`, undefined, {
    paymentMethodType,
    timeoutMs,
  });

/**
 * The previous mount settles quickly in every healthy case (its form aborted
 * it, or it finished); a mount that never settles is broken, and blocking every
 * later method behind it would only turn one broken method into all of them.
 */
const awaitPreviousPaymentComponentMount = (previous: Promise<unknown>, paymentMethodType: string): Promise<void> =>
  awaitPaymentComponentSignal(previous.then(noop, noop), {
    timeoutMs: CHARGEBEE_MOUNT_HANDOVER_TIMEOUT_MS,
    onTimeout: () =>
      logError('Previous Chargebee payment component mount never settled, mounting anyway', undefined, {
        paymentMethodType,
        timeoutMs: CHARGEBEE_MOUNT_HANDOVER_TIMEOUT_MS,
      }),
  });

const noop = (): void => undefined;

export class ChargebeeMountAbortedError extends Error {
  constructor() {
    super('Chargebee payment component mount aborted');
    this.name = 'ChargebeeMountAborted';
  }
}

/** Tears the half-mounted component down and stops the mount — nothing after this may touch the DOM. */
const throwIfAborted = (signal: AbortSignal | undefined, component?: { close: () => void }): void => {
  if (!signal?.aborted) return;
  component?.close();
  throw new ChargebeeMountAbortedError();
};

/** Chargebee's Pay button for the component; `null` when Chargebee refuses to mount it. */
const mountPaymentButton = async (
  components: ChargebeeComponents,
  button: { selector: string; text: string },
  paymentMethodType: string,
  onFailed?: (error: ChargebeePaymentComponentError) => void
): Promise<ChargebeePaymentButtonComponent | null> => {
  const buttonComponent = components.create(
    'payment-button',
    { paymentMethods: { options: { [paymentMethodType]: { text: button.text } } } },
    { onError: (error) => onFailed?.(error) }
  );
  clearWalletContainer(button.selector);
  return (await buttonComponent.mount(button.selector)) ? buttonComponent : null;
};

/**
 * Hydration is best-effort at boot: the retrieval is a plain read, so a blip
 * shouldn't cost the shopper the wallet buttons for the rest of the session.
 */
export const retryChargebeePaymentIntent = async (
  paymentIntentId: string,
  attempts: number,
  delayMs: number
): Promise<ChargebeePaymentIntent> => {
  for (let attempt = 1; ; attempt++) {
    try {
      return await chargebeeService.retrievePaymentIntent(paymentIntentId);
    } catch (error) {
      if (attempt >= attempts) throw error;
      logError('Chargebee payment intent retrieval failed, retrying', error, { paymentIntentId, attempt });
      await new Promise((resolve) => setTimeout(resolve, delayMs * attempt));
    }
  }
};
